import React, { useEffect, useRef, useState, forwardRef } from "react";
import {
  SFStack,
  SFTypography,
  SFUseStyled,
  SFGridContainer,
  SFGridItem,
  SFProgressBar,
  Colors,
  SFEmptyPlaceholder,
  SFIcons,
} from "@sf-core";
import { STATUS } from "../../constants/statuses";
import { DOMAIN_TYPES } from "../../constants/domainTypes";
import { NUM_STATUS } from "../../constants/statuses";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { getAcademyUrl, getJobUrl, getSkillUrl } from "../../constants/urls";

const { styled } = SFUseStyled();

const Container = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  overflow: "auto",
  height: 200,
  minWidth: 200,
  padding: theme.spacing(1, 0),
  width: "100%",
  "&::-webkit-scrollbar": {
    display: "none",
  },
}));

const SectionContainer = styled("div", {
  shouldForwardProp: (prop) => prop !== "hasData",
})(({ theme, hasData }) => ({
  width: "100%",
  marginBottom: "40px",
  ...(!hasData && {
    marginBottom: "100px",
  }),
}));

const StyledCard = styled(SFStack)(({ theme }) => ({
  borderRadius: theme.spacing(1),
  padding: theme.spacing(0, 1.5),
  background:
    "linear-gradient(180deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)",
  height: 70,
  cursor: "pointer",
  "&:hover": {
    background: "rgba(255, 255, 255, 0.12)",
  },
}));

const StyledDivider = styled("div")(({ theme }) => ({
  width: "100%",
  borderBottom: "1px solid",
  borderColor: "rgba(255, 255, 255, 0.15)",
  paddingBottom: theme.spacing(0.5),
  marginBottom: theme.spacing(1.5),
  textTransform: "uppercase",
}));

const StyledDot = styled("div", {
  shouldForwardProp: (prop) => prop !== "active",
})(({ theme, active }) => ({
  width: 8,
  height: 8,
  borderRadius: "50%",
  cursor: "pointer",
  background: theme.palette.grey400.main,
  ...(active && {
    background: theme.palette.grey300.main,
  }),
}));

// Section Types
const SECTION_TYPE = { academy: 0, job: 1, skillGoal: 2 };

const Section = forwardRef(
  ({ id, onScroll, data = [], onClick, color }, ref) => {
    const { t } = useTranslation();
    useEffect(() => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            onScroll(id, true);
          } else {
            onScroll(id, false);
          }
        },
        { threshold: data.length > 1 ? 1 / (data.length - 1) : 1 }
      );

      const currentRef = ref?.current; // Store current ref
      if (currentRef) {
        observer.observe(currentRef);
      }

      return () => {
        if (currentRef) {
          observer.unobserve(currentRef); // Unobserve stored ref
        }
      };
    }, [id, onScroll, ref]);

    return (
      <SectionContainer ref={ref} hasData={data.length > 0}>
        <SFGridContainer spacing={1}>
          {data.length > 0 ? (
            data.map((item, index) => (
              <SFGridItem key={index} xs={12} md={12} lg={6}>
                <StyledCard
                  onClick={() => onClick(item.id)}
                  justifyContent="center"
                >
                  <SFTypography variant="body1" maxLines={1}>
                    {item.name}
                  </SFTypography>
                  <SFTypography variant="textLabel">
                    {item.type === DOMAIN_TYPES.USER_JOB_GOAL
                      ? t("homeScreen.heroes.progressCards.jobGoalLabel")
                      : item.type === DOMAIN_TYPES.USER_SELECTED_JOB
                        ? t("homeScreen.heroes.progressCards.userJobLabel")
                        : ""}
                  </SFTypography>
                  <SFStack alignItems="flex-end">
                    <SFStack direction="row" spacing={1}>
                      <SFStack
                        spacing={0.5}
                        direction="row"
                        alignItems="center"
                      >
                        {item.status === NUM_STATUS.IN_PROGRESS && (
                          <SFIcons.SFBookIcon size={"xsmall"} color="grey200" />
                        )}
                        <SFTypography variant="textLabel">
                          {item.status > NUM_STATUS.NOT_STARTED
                            ? item.status == NUM_STATUS.COMPLETED
                              ? STATUS.COMPLETED
                              : item.completedCourses + "/" + item.totalCourses
                            : STATUS.NOT_STARTED}
                        </SFTypography>
                      </SFStack>
                      {item.status === NUM_STATUS.IN_PROGRESS &&
                      item.skillPaths > 0 ? (
                          <SFStack
                            spacing={0.5}
                            direction="row"
                            alignItems="center"
                          >
                            <SFIcons.SFMapIcon size={"xsmall"} color="grey200" />
                            <SFTypography variant="textLabel">
                              {item.status > NUM_STATUS.NOT_STARTED
                                ? item.status == NUM_STATUS.COMPLETED
                                  ? STATUS.COMPLETED
                                  : item.completedSkillPaths +
                                  "/" +
                                  item.skillPaths
                                : STATUS.NOT_STARTED}
                            </SFTypography>
                          </SFStack>
                        ) : null}
                    </SFStack>
                    <SFProgressBar
                      value={
                        (item.completedDuration / item.totalDuration) * 100
                      }
                      narrow
                      color={color}
                      completeColor={color}
                    />
                  </SFStack>
                </StyledCard>
              </SFGridItem>
            ))
          ) : (
            <SFGridItem xs={12}>
              <SFEmptyPlaceholder />
            </SFGridItem>
          )}
        </SFGridContainer>
      </SectionContainer>
    );
  }
);

const ProgressCards = ({ data = [] }) => {
  const { t } = useTranslation();
  const [sectionsStatus, setSectionsStatus] = useState({
    [SECTION_TYPE.academy]: true,
    [SECTION_TYPE.job]: false,
    [SECTION_TYPE.skillGoal]: false,
  });
  const [activeSection, setActiveSection] = useState(0);
  const [sections, setSections] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    setSections([]);
    data &&
      data.map((sectionData, i) => {
        const section = +Object.keys(sectionData)[0];
        switch (section) {
        case SECTION_TYPE.academy:
          setSections((prev) => [
            ...prev,
            {
              title: t("homeScreen.heroes.progressCards.academies"),
              data: sectionData[section],
              onClick: (id) => navigate(getAcademyUrl(id)),
              color: Colors.TEAL500,
            },
          ]);
          break;
        case SECTION_TYPE.job:
          setSections((prev) => [
            ...prev,
            {
              title: t("homeScreen.heroes.progressCards.jobs"),
              data: sectionData[section],
              onClick: (id) => navigate(getJobUrl(id)),
              color: Colors.MAROON300,
            },
          ]);
          break;
        case SECTION_TYPE.skillGoal:
          setSections((prev) => [
            ...prev,
            {
              title: t("homeScreen.heroes.progressCards.skillGoals"),
              data: sectionData[section],
              onClick: (id) => navigate(getSkillUrl(id)),
              color: Colors.BLUE500,
            },
          ]);
          break;
        default:
          break;
        }
      });
  }, [data]);

  // Create refs for each section
  const sectionRefs = Array(3)
    .fill()
    .map(() => useRef());

  const containerRef = useRef();

  const onScroll = (id, isIntersecting) => {
    switch (id) {
    case SECTION_TYPE.academy:
      setSectionsStatus((prev) => ({
        ...prev,
        [SECTION_TYPE.academy]: isIntersecting,
      }));
      break;
    case SECTION_TYPE.job:
      setSectionsStatus((prev) => ({
        ...prev,
        [SECTION_TYPE.job]: isIntersecting,
      }));
      break;
    case SECTION_TYPE.skillGoal:
      setSectionsStatus((prev) => ({
        ...prev,
        [SECTION_TYPE.skillGoal]: isIntersecting,
      }));
      break;
    default:
      break;
    }

    if (sectionsStatus[SECTION_TYPE.academy])
      setActiveSection(SECTION_TYPE.academy);
    else if (sectionsStatus[SECTION_TYPE.job])
      setActiveSection(SECTION_TYPE.job);
    else if (sectionsStatus[SECTION_TYPE.skillGoal])
      setActiveSection(SECTION_TYPE.skillGoal);
  };

  const scrollToSection = (id) => {
    let top;
    const cardTopPadding = 66; // Padding between the first card and the section title
    const cardMargin = 78; // Margin between cards
    const extraPadding = 126; // Extra padding for the section if there are no cards
    let jobPadding =
      sections[0]?.data.length > 0 ? cardTopPadding : extraPadding;
    let skillPadding =
      sections[1]?.data.length > 0 ? cardTopPadding : extraPadding;
    if (id == 1) {
      // Calculate the top position of the second section
      top = Math.ceil(sections[0]?.data.length / 2) * cardMargin + jobPadding;
    } else if (id == 2) {
      // Calculate the top position of the third section
      top =
        Math.ceil(sections[0]?.data.length / 2) * cardMargin +
        Math.ceil(sections[1]?.data.length / 2) * cardMargin +
        +jobPadding +
        skillPadding;
    } else {
      // Zero top position for the first section
      top = 0;
    }
    if (containerRef.current) {
      containerRef.current.scrollTo({ top: top, behavior: "smooth" });
    }
  };

  return (
    <SFStack direction="row" spacing={2}>
      <Container ref={containerRef}>
        {sections.length > 0 &&
          sections.map((section, i) => {
            return (
              <React.Fragment key={i}>
                <StyledDivider>
                  <SFTypography variant="textLabel">
                    {section.title}
                  </SFTypography>
                </StyledDivider>
                <Section
                  id={i}
                  ref={sectionRefs[i]}
                  onScroll={onScroll}
                  data={section.data}
                  type={section.type}
                  onClick={section.onClick}
                  color={section.color}
                />
              </React.Fragment>
            );
          })}
      </Container>
      <SFStack spacing={1} style={{ width: 20 }} justifyContent="center">
        {Array(3)
          .fill()
          .map((_, i) => (
            <StyledDot
              key={i}
              active={i == activeSection}
              onClick={() => scrollToSection(i)}
            />
          ))}
      </SFStack>
    </SFStack>
  );
};

export default ProgressCards;
